import { createPluralFormatter } from 'helpers/formatHelpers'
import { AccountUid } from 'interfaces/linkage'
import { State } from 'interfaces/state'
import { createSelector } from 'reselect'
import { manualBalanceAccountsSelector, providerBalanceAccountsSelector } from 'selectors/linkageSelectors'

export const pageFiltersSelector = (state: State) => state.app.pageFilters

export const withComparisonSelector = createSelector(
  pageFiltersSelector,
  (filtersState) => {
    return filtersState.comparison
  }
)

const disabledAccountsUidsSelector = createSelector(
  pageFiltersSelector,
  (filtersSet) => filtersSet.disabled_accounts,
)

export const disabledAccountsUidsSetSelector = createSelector(
  disabledAccountsUidsSelector,
  (disabled_accounts) => new Set(disabled_accounts)
)

const accountsUidsMapSelector = createSelector(
  providerBalanceAccountsSelector,
  manualBalanceAccountsSelector,
  (providerBalanceAccounts, manualBalanceAccounts) => {

    const byKey = {
      ...providerBalanceAccounts?.byKey ?? {},
      ...manualBalanceAccounts?.byKey ?? {},
    }

    type ValueOfType = typeof byKey[keyof typeof byKey]

    const result = Object.keys(byKey).reduce((acc, key) => {
      const account = byKey[key]
      acc[account.uid] = account
      return acc
    }, {} as Record<AccountUid, ValueOfType>)

    return result
  }
)

export const accountsEnabledStateUidsMapSelector = createSelector(
  disabledAccountsUidsSetSelector,
  accountsUidsMapSelector,
  (disabledAccountsUidsSet, accountsUidsMap) => {

    const result = Object.keys(accountsUidsMap).reduce((acc, uid) => {
      acc[uid] = !disabledAccountsUidsSet.has(uid)
      return acc
    }, {} as Record<AccountUid, boolean>)

    return result
  }
)

const enabledAccountsUidsSelector = createSelector(
  accountsEnabledStateUidsMapSelector,
  (accountsEnabledStateUidsMap) => {

    return Object.keys(accountsEnabledStateUidsMap)
      .filter((uid) => accountsEnabledStateUidsMap[uid])
  }
)

export const accountsFilterQueryParamSelector = createSelector(
  disabledAccountsUidsSelector,
  (disabledAccountsUids) => {
    if (!disabledAccountsUids || disabledAccountsUids.length === 0) {
      return null
    }

    return disabledAccountsUids.join(',')
  }
)

const accountsPlural = createPluralFormatter('Account', 'Accounts')
export const enabledAccountsTitleSelector = createSelector(
  enabledAccountsUidsSelector,
  accountsUidsMapSelector,
  providerBalanceAccountsSelector,
  manualBalanceAccountsSelector,
  (enabledAccounts, accountsUidsMap) => {
    if (enabledAccounts === null) {
      return {
        title: 'All Accounts',
        count: Object.keys(accountsUidsMap).length,
      }
    }

    return {
      title: `${ enabledAccounts.length } ${ accountsPlural(enabledAccounts.length) }`,
      count: enabledAccounts.length,
    }
  }
)
